import React, { useCallback, useMemo } from 'react';

import { GUID, PersonOrg, PersonOrgType } from '@ndvr/customer-api';

import { FormSheet } from '@/components/FormSheet';
import { EntityFormFields, TrustFormFields } from 'modules';
import { getOrgName, useRequiredParams } from 'utils';
import { OrgFormValues } from 'views/person/person-form-model';

import { getOrgFormDefaultValues } from '../views/person/person-form-utils';
import { useOrgLinks, usePersonOrgProfile, useSaveOrgProfile } from '../views/person/person-queries';

export type OrgType = PersonOrgType.ORGANIZATION | PersonOrgType.TRUST;

export const OrgDrawer = ({
    onClose,
    orgType,
    orgGUID,
}: {
    onClose: () => void;
    orgType: OrgType;
    orgGUID?: GUID<PersonOrg>;
}) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);

    const { data: orgProfile, isPending: orgProfileLoading } = usePersonOrgProfile(orgGUID);
    const { data: orgLinks, isPending: orgLinksLoading } = useOrgLinks(orgGUID);
    const { mutate: saveOrgProfile, isPending: saveInProgress } = useSaveOrgProfile({ globalAccountGUID, orgGUID });

    const defaultValues: OrgFormValues = useMemo(() => {
        return orgProfile ? getOrgFormDefaultValues(orgProfile, orgLinks || []) : { personOrgType: orgType };
    }, [orgLinks, orgProfile, orgType]);

    const onSubmit = useCallback(
        (formData: OrgFormValues, close: () => void) => {
            saveOrgProfile(formData, { onSuccess: close });
        },
        [saveOrgProfile],
    );

    return (
        <FormSheet
            size="lg"
            title={
                orgGUID
                    ? `Edit ${getOrgName(orgProfile?.personOrg)}`
                    : `New ${orgType === PersonOrgType.TRUST ? 'Trust' : 'Entity'}`
            }
            onClose={onClose}
            onSubmit={onSubmit}
            defaultValues={defaultValues}
            isLoading={!!orgGUID && (orgProfileLoading || orgLinksLoading)}
            pending={saveInProgress}
            submitLabel={orgGUID ? 'Save' : 'Create'}
        >
            {orgType === PersonOrgType.TRUST ? <TrustFormFields /> : <EntityFormFields />}
        </FormSheet>
    );
};
